import classNames from "classnames";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import FalconComponentCard from "components/common/FalconComponentCard";
import IconButton from "components/common/IconButton";
import MultiSelect from "components/common/MultiSelect";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Col, Collapse, Form, Row } from "react-bootstrap";
import { Gear, Plus, Trash } from "react-bootstrap-icons";
import DataTable from "react-data-table-component";
import { Controller } from "react-hook-form"; // Make sure this import exists
import { useDispatch, useSelector } from "react-redux";
import {
  handlePreauthorizationDiagnosis,
  removeValidationWarningByCode,
} from "../config/preauthorizationSlice";
import useAdvanceTable from "hooks/useAdvanceTable";
import { useAppContext } from "providers/AppProvider";
import { motion } from "framer-motion";
import DiagnosisCodeSelect from "./DiagnosisCode Select";
import DiagnosisMorphologySelect from "./DiagnosisMorphologySelect";
import { getToday } from "helpers/utils";
import WizardModal from "components/wizard/WizardModal";
import LabTest from "./LabTest";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  GetAllClaimPreAuthAllList,
  GetMorphologyCodeDescription,
  GetServiceCodeDescription,
} from "../config/preauthorizationApi";
import PreauthLabTest from "./PreauthLabTest";

const DiagnosisRequest = ({
  watch,
  control,
  setValue,
  activeStep,
  setError,
  touchedFields,
}) => {
  const { stepFormData } = useSelector((state) => state.preauthorization);

  const isPrescription = stepFormData?.prescription;
  return (
    <div className="flex flex-column gap-3">
      <Accident watch={watch} control={control} setValue={setValue} touchedFields={touchedFields} />
      <Diagnosis
        watch={watch}
        control={control}
        setValue={setValue}
        activeStep={activeStep}
        setError={setError}
      />
      <Morphology setValue={setValue} control={control} watch={watch} />
      {!isPrescription && (
        <Col xs={12}>
          <PreauthLabTest control={control} setValue={setValue} watch={watch} />
        </Col>
      )}
    </div>
  );
};

export default DiagnosisRequest;

const Diagnosis = ({ watch, control, setValue }) => {
  const dispatch = useDispatch();
  const {
    preauthorizationDiagnosis,
    diagnosisiList,
    conditionOnset,
    validationWarning,
    preauthorizationRequest,
    stepFormData,
    viewPreauthorizationInfo,
  } = useSelector((state) => state.preauthorization);

  const [showTypeError, setShowTypeError] = useState(false);
  const [searchParams] = useSearchParams();
  const reuse = searchParams.get("reuse") === "true";
  const edit = searchParams.get("edit") === "true";
  const followUp = searchParams.get("followup") === "true";
  const isInstitual =
    stepFormData?.preauthorization_type?.claim_types_name === "Institutional";

  useEffect(() => {
    if (diagnosisiList?.length === 0) {
      dispatch(GetAllClaimPreAuthAllList());
    }
  }, []);

  // Process all diagnoses from provider_request
  useEffect(() => {
    const fillAndAddDiagnoses = async () => {
      // Exit early if conditions are not met
      if (
        (!reuse && !edit && !followUp) ||
        !viewPreauthorizationInfo?.provider_request ||
        !viewPreauthorizationInfo.provider_request.diagnosis?.length ||
        preauthorizationDiagnosis?.length > 0 // Prevent re-adding if already populated
      ) {
        return;
      }

      try {
        const diagnosisData =
          viewPreauthorizationInfo.provider_request.diagnosis;
        const uniqueEntries = new Map();

        diagnosisData?.forEach((diag) => {
          const code = diag?.diagonis_code;
          const type = diag?.diagonis_type;

          // Safe fallback for onAdmission
          const onAdmissionRaw = diag?.diagonis_onadms;
          let onAdmission = "";
          if (onAdmissionRaw) {
            const lower = onAdmissionRaw.toLowerCase();
            if (lower === "yes") onAdmission = "y";
            else if (lower === "no") onAdmission = "n";
            else if (lower === "unknown") onAdmission = "u";
          }

          // Safe fallback for condition_onset
          const conditionOnsetCode = diag?.diagonis_onset || "";

          // Skip if code or type is missing
          if (!code || !type) return;

          const key = `${code}|${type}`;
          if (!uniqueEntries.has(key)) {
            uniqueEntries.set(key, {
              ...diag,
              onAdmission,
              conditionOnsetCode,
            });
          }
        });

        for (const [, diag] of uniqueEntries.entries()) {
          const diagnosisCode = diag?.diagonis_code;
          const diagnosisType = diag?.diagonis_type;
          const onAdmission = diag?.onAdmission || "";
          const conditionOnsetCode = diag?.conditionOnsetCode || "";

          const exists = preauthorizationDiagnosis.some(
            (d) => d.code === diagnosisCode && d.type === diagnosisType
          );
          if (exists) continue;

          const response = await dispatch(
            GetServiceCodeDescription({ diag_code: diagnosisCode })
          ).unwrap();

          const data = response?.data?.Diagnosis_code?.[0] || {};

          dispatch(
            handlePreauthorizationDiagnosis({
              type: "add",
              payload: {
                id: new Date(),
                date: moment().format("DD-MM-YYYY"),
                type: diagnosisType,
                code: diagnosisCode,
                description:
                  data?.icd_description || diag?.diagonis_description || "",
                valid: data?.valid,
                effective_from: data?.effective_from,
                inactive: data?.inactive,
                reactivated: data?.reactivated,
                sex: data?.sex,
                Stype: data?.Stype,
                AgeL: data?.AgeL,
                AgeH: data?.AgeH,
                Atype: data?.Atype,
                RDiag: data?.RDiag,
                Morph_Code: data?.Morph_Code,
                RTA_diag: data?.RTA_diag,
                concept_change: data?.concept_change,
                UnacceptPDx: data?.UnacceptPDx,
                condition_onset: conditionOnsetCode,
                onAdmission: onAdmission,
              },
            })
          );
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Diagnosis loading failed:", error);
        }
      }
    };

    fillAndAddDiagnoses();
  }, [
    reuse,
    edit,
    followUp,
    viewPreauthorizationInfo,
  ]);

  useEffect(() => {
    const isOutPatient =
      stepFormData?.preauthorization_sub_type?.visitreas_name === "OutPatient";
    if (preauthorizationDiagnosis?.length === 0 || !preauthorizationDiagnosis) {
      setValue(
        "diagnosis_type",
        diagnosisiList?.filter((item) => item?.diagtype_code === "principal")[0]
      );
    }

    if (isOutPatient) {
      setValue(
        "condition_onset",
        conditionOnset?.filter((item) => item?.condonset_code === "NR")[0]
      );
    }
  }, [preauthorizationDiagnosis, diagnosisiList, stepFormData]);

  const {
    config: { theme },
  } = useAppContext();

  const [isAdd, setIsAdd] = useState(false);

  const deleteRow = (row) => {
    dispatch(
      handlePreauthorizationDiagnosis({
        type: "delete",
        payload: { id: row?.id },
      })
    );

    const exists = validationWarning.some((warn) => warn.code === row?.code);

    if (exists) {
      dispatch(removeValidationWarningByCode(row?.code));
    }
  };

  const hanldeUpdateType = (value, id, type) => {
    // Check for principal type and Y96 code
    if (type === "type" && value === "principal") {
      const diagnosis = preauthorizationDiagnosis.find((d) => d.id === id);
      if (diagnosis?.code === "Y96") {
        setShowTypeError(true);
        return; // Prevent dispatch if invalid
      }
    }

    dispatch(
      handlePreauthorizationDiagnosis({
        type: "update",
        payload: { id, field: type, value },
      })
    );
  };

  const columns = [
    {
      name: "",
      sortable: false,
      omit: false,
      selector: (_, index) => (
        <div
          className="rounded-circle bg-200 d-flex flex-center fs-10 mx-auto"
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
          }}
        >
          {index + 1}
        </div>
      ),
      width: "4rem",
    },
    {
      name: "Date",
      sortable: false,
      omit: false,
      selector: (row, _) => (
        <div className="text-center mx-auto fs-10">{row?.date || "-"}</div>
      ),
      width: "8rem",
    },
    {
      name: "Type",
      sortable: false,
      omit: false,
      cell: (row, _) => {
        return (
          <div className="d-flex">
            <Form.Select
              value={`${row?.type}` || ""}
              className="fs-10"
              onChange={(e) =>
                hanldeUpdateType(e.target.value, row?.id, "type")
              }
            >
              <option value="">Select Type</option>
              {diagnosisiList?.map((item) => (
                <option value={item?.diagtype_code}>
                  {item?.diagtype_name}
                </option>
              ))}
            </Form.Select>
          </div>
        );
      },
    },
    {
      name: "Condition Onset",
      sortable: false,
      omit: false,
      cell: (row, _) => {
        return (
          <div className="d-flex">
            <Form.Select
              value={`${row?.condition_onset}` || ""}
              className="fs-10"
              onChange={(e) =>
                hanldeUpdateType(e.target.value, row?.id, "condition_onset")
              }
            >
              <option value="">Select Type</option>
              {conditionOnset?.map((item) => (
                <option value={item?.condonset_code}>
                  {item?.condonset_name}
                </option>
              ))}
            </Form.Select>
          </div>
        );
      },
    },
    ...(isInstitual
      ? [
        {
          name: "On Admission",
          sortable: false,
          omit: false,
          cell: (row, _) => {
            return (
              <div className="d-flex">
                <Form.Select
                  value={`${row?.onAdmission}` || ""}
                  className="fs-10"
                  onChange={(e) =>
                    hanldeUpdateType(e.target.value, row?.id, "onAdmission")
                  }
                >
                  <option value="">Select Type</option>
                  <option value="y">Yes</option>
                  <option value="n">No</option>
                  <option value="u">Unknown</option>
                </Form.Select>
              </div>
            );
          },
        },
      ]
      : []),
    {
      name: "Diagnosis Info",
      sortable: false,
      omit: false,
      selector: (row) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          ({row?.code}) - {row?.description}
        </div>
      ),
    },
    {
      name: <Gear />,
      button: true,
      cell: (row) => (
        <IconButton size="sm" variant="danger" onClick={() => deleteRow(row)}>
          <Trash />
        </IconButton>
      ),
    },
  ];

  const addDiagnosis = () => {
    const diagnosisCode = watch("diagnosis_code_discription");
    const diagnosisType = watch("diagnosis_type")?.diagtype_code;
    const admission = watch("onAdmission");
    const morphology = watch("morphology");

    // New validation check
    if (diagnosisType === "principal" && diagnosisCode?.icd_code === "Y96") {
      setShowTypeError(true);
      return;
    }

    // Rest of existing validation
    if (!watch("diagnosis_date") || !diagnosisType || !diagnosisCode) {
      setIsAdd(true);
      return;
    }

    if (isInstitual && !admission) {
      setIsAdd(true);
      return;
    }

    if (
      diagnosisCode?.Morph_Code === 1 &&
      ["principal", "secondary"].includes(diagnosisType) &&
      !morphology
    ) {
      // Morphology validation
      dispatch(
        addValidationError({
          field: "morphology",
          message: "Morphology is required for this diagnosis code",
        })
      );
      return;
    }

    dispatch(
      handlePreauthorizationDiagnosis({
        type: "add",
        payload: {
          id: Date.now(),
          date: moment(watch("diagnosis_date")).format("DD-MM-YYYY"),
          type: diagnosisType,
          code: diagnosisCode?.icd_code,
          description: diagnosisCode?.icd_description,
          level: diagnosisCode?.level,
          effective_from: diagnosisCode?.effective_from,
          inactive: diagnosisCode?.inactive,
          reactivated: diagnosisCode?.reactivated,
          valid: diagnosisCode?.valid,
          sex: diagnosisCode?.sex,
          Stype: diagnosisCode?.Stype,
          AgeL: diagnosisCode?.AgeL,
          AgeH: diagnosisCode?.AgeH,
          Atype: diagnosisCode?.Atype,
          RDiag: diagnosisCode?.RDiag,
          Morph_Code: diagnosisCode?.Morph_Code,
          RTA_diag: diagnosisCode?.RTA_diag,
          concept_change: diagnosisCode?.concept_change,
          UnacceptPDx: diagnosisCode?.UnacceptPDx,
          condition_onset: watch("condition_onset")?.condonset_code || "",
          onAdmission: watch("onAdmission") || "",
        },
      })
    );

    // Reset form fields
    setValue("diagnosis_date", getToday());
    setValue("diagnosis_type", null);
    setValue("diagnosis_code_discription", null);
    setValue("condition_onset", null);
    setValue("morphology", null); // Reset morphology
    setValue("onAdmission", null);
    setIsAdd(false);
  };

  return (
    <div
      style={{ border: "2px solid #d9f8eb" }}
      className="rounded p-3 position-relative  mt-4 mb-4"
    >
      <div className="position-absolute top-0 start-45 translate-middle-y px-2">
        <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
          <span className="fs-10">Diagnosis</span>
        </span>
      </div>
      <Row className="align-items-center">
        <Col xs={12} sm={6} md={4} xl={2}>
          <BootDatePicker
            name="diagnosis_date"
            label="Diagnosis Date"
            control={control}
            rules={{ required: false }}
            size="sm"
            className={classNames({
              "is-invalid":
                isAdd &&
                (watch("diagnosis_date")?.value === "" ||
                  !watch("diagnosis_date")),
            })}
            isRequired={false}
            placeholder="dd/mm/yyyy"
            selected={watch("diagnosis_date")}
          />
        </Col>
        <Col xs={12} sm={6} md={4} xl={2}>
          <label className="form-label">Diagnosis Type </label>
          <Controller
            name="diagnosis_type"
            control={control}
            rules={{ required: false }}
            render={({ field, ref }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                ref={ref}
                closeMenuOnSelect={false}
                value={
                  field?.value
                    ? {
                      value: field?.value?.diagtype_code,
                      label: field?.value?.diagtype_name,
                    }
                    : null
                }
                className={classNames({
                  "is-invalid":
                    isAdd &&
                    (watch("diagnosis_type")?.value === "" ||
                      !watch("diagnosis_type")),
                })}
                isMulti={false}
                onChange={(selected) => {
                  setValue(
                    "diagnosis_type",
                    selected
                      ? {
                        diagtype_code: selected?.value,
                        diagtype_name: selected?.label,
                      }
                      : null
                  );
                }}
                formatOptionLabel={({ label }) => <div>{label}</div>}
                getOptionValue={(option) => option.value}
                options={diagnosisiList?.map((item) => ({
                  value: item?.diagtype_code,
                  label: item?.diagtype_name,
                }))}
                placeholder="Choose ... "
              />
            )}
          />
        </Col>
        <Col xs={12} sm={6} md={4} xl={2}>
          <label className="form-label">Condition Onset</label>
          <Controller
            name="condition_onset"
            control={control}
            rules={{ required: false }}
            render={({ field, ref }) => (
              <MultiSelect
                {...field}
                menuPlacement="auto"
                size="sm"
                ref={ref}
                closeMenuOnSelect={false}
                value={
                  field?.value
                    ? {
                      value: field?.value?.condonset_code,
                      label: field?.value?.condonset_name,
                    }
                    : null
                }
                isMulti={false}
                onChange={(selected) => {
                  setValue(
                    "condition_onset",
                    selected
                      ? {
                        condonset_code: selected?.value,
                        condonset_name: selected?.label,
                      }
                      : null
                  );
                }}
                formatOptionLabel={({ label }) => <div>{label}</div>}
                getOptionValue={(option) => option.value}
                options={conditionOnset?.map((item) => ({
                  value: item?.condonset_code,
                  label: item?.condonset_name,
                }))}
                placeholder="Choose ... "
              />
            )}
          />
        </Col>

        {/* Add onAdmission field conditionally */}
        {isInstitual && (
          <Col xs={12} sm={6} md={4} xl={2}>
            <label className="form-label">On Admission</label>
            <Controller
              name="onAdmission"
              control={control}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  value={field.value || ""} // Use field.value instead of watch
                  size="sm"
                  className={classNames({
                    "is-invalid":
                      isAdd &&
                      (watch("onAdmission") === "" || !watch("onAdmission")),
                  })}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="y">Yes</option>
                  <option value="n">No</option>
                  <option value="u">Unknown</option>
                </Form.Select>
              )}
            />
          </Col>
        )}

        <Col xs={12} md={6} xl={isInstitual ? 4 : 6}>
          <Row className="align-items-center justify-content-between">
            <DiagnosisCodeSelect
              control={control}
              isAdd={isAdd}
              preauthorizationDiagnosis={preauthorizationDiagnosis}
              setValue={setValue}
              watch={watch}
            />
            <Col xs={2}>
              <IconButton
                size="sm"
                variant="success"
                className="mt-4"
                onClick={addDiagnosis}
              >
                <Plus size={20} />
              </IconButton>
            </Col>
          </Row>
        </Col>
        {preauthorizationDiagnosis?.length > 0 && (
          <Col xs={12} className="mt-3">
            <DataTable
              columns={columns}
              theme={theme}
              data={preauthorizationDiagnosis}
              responsive
              fixedHeader
            />
          </Col>
        )}
      </Row>
      <WizardModal
        modal={showTypeError}
        setModal={() => setShowTypeError(false)}
        title="Type Wrong"
        description="This Type can't be Principle"
      />
    </div>
  );
};

const Morphology = ({ control, setValue, watch }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { preauthorizationDiagnosis, viewPreauthorizationInfo } = useSelector(
    (state) => state.preauthorization
  );

  const reuse = searchParams.get("reuse") === "true";
  const edit = searchParams.get("edit") === "true";
  const followUp = searchParams.get("followup") === "true";
  const hasPrincipleWithMorphCode = preauthorizationDiagnosis?.some(
    (item) =>
      (item.type === "principal" || item?.type === "secondary") &&
      item.Morph_Code === "1"
  );

  const morphologyCode = viewPreauthorizationInfo?.provider_request?.medical_details?.morphology;


  useEffect(() => {
    const fetchMorphology = async () => {
      if ((!reuse && !edit && !followUp) || !morphologyCode) return;

      try {
        const response = await dispatch(
          GetMorphologyCodeDescription({ morph_code: morphologyCode })
        ).unwrap();

        const data = response?.data?.Morphology_Code?.[0];

        setValue("diagnosis_morphology", data);
      } catch (error) {
        console.error("Failed to fetch morphology:", error);
      }
    };

    fetchMorphology();
  }, [reuse, edit, followUp]);

  return (
    <div
      style={{ border: "2px solid #d9f8eb" }}
      className="rounded p-3 position-relative  mt-2"
    >
      <div className="position-absolute top-0 start-45 translate-middle-y px-2">
        <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
          <span className="fs-10">Morphology</span>
        </span>
      </div>
      <Col xs={12} className="mt-2">
        <Row className="align-items-center">
          <Col xs={2}>
            <h5 className="fs-10">Morphology</h5>
          </Col>
          <Col xs={10}>
            <DiagnosisMorphologySelect
              control={control}
              setValue={setValue}
              watch={watch}
              required={hasPrincipleWithMorphCode}
            />
          </Col>
        </Row>
      </Col>
    </div>
  );
};

const Accident = ({ watch, control, setValue, touchedFields }) => {
  const [searchParams] = useSearchParams();
  const { accidentType, preauthorizationDiagnosis, viewPreauthorizationInfo } =
    useSelector((state) => state.preauthorization);
  const isAccident = preauthorizationDiagnosis?.some(
    (item) => item?.code === "Y96"
  );

  const isMotor = preauthorizationDiagnosis?.some(
    (item) => item?.RTA_diag === "1"
  );


  const reuse = searchParams.get("reuse") === "true";
  const edit = searchParams.get("edit") === "true";
  const followUp = searchParams.get("followup") === "true";

  const isAccidentInfo = watch("accident");

  const accidentInfo = viewPreauthorizationInfo?.provider_request?.accident;

  useEffect(() => {
    if (isAccidentInfo) {
      const accTypeCode = isMotor ? "MVA" : isAccident ? "WPA" : null;
      const accType = accidentType?.find(
        (item) => item.accdnttype_code === accTypeCode
      );

      if (accType) {
        setValue("accident_type", {
          accdnttype_code: accType.accdnttype_code,
          accdnttype_name: accType.accdnttype_name,
        });
      }
      setValue("accident_date", getToday());
    } else if (reuse || edit || followUp) {
      setValue(
        "accident",
        touchedFields?.accident ? watch("accident") : accidentInfo?.type
      ); setValue(
        "accident_date",
        watch("accident_date") || accidentInfo?.date
          ? new Date(accidentInfo?.date)
          : ""
      );
      setValue(
        "accident_type",
        watch("accident_type") ||
        accidentType?.find(
          (item) =>
            item?.accdnttype_code ==
            accidentInfo?.type
        )
      );
      setValue(
        "address",
        accidentInfo?.address
      );
    } else {
      // Accident turned off — clear values
      setValue("accident_type", null);
      setValue("accident_date", null);
    }
  }, [
    isAccidentInfo,
    isAccident,
    reuse,
    edit,
    followUp,
    isMotor,
    accidentType,
    accidentInfo,
  ]);

  return (
    <div
      style={{ border: "2px solid #d9f8eb" }}
      className="rounded p-3 position-relative mt-2"
    >
      <div className="position-absolute top-0 start-45 translate-middle-y px-2">
        <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
          <span className="fs-10">Accident</span>
        </span>
      </div>

      <Col xs={12}>
        <Row className="align-items-center">
          <Col xs={8} md={2}>
            <h5 className="fs-10">Accident?</h5>
          </Col>
          <Col xs={4} md={10}>
            <Controller
              name="accident"
              control={control}
              rules={{ required: isAccident }}
              defaultValue={isAccident}
              render={({ field }) => (
                <Form.Switch
                  {...field}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  size="lg"
                />
              )}
            />
          </Col>
        </Row>
      </Col>

      {watch("accident") && (
        <Row className="align-items-end">
          <Col xs={12} sm={6} md={4} xl={3}>
            <BootDatePicker
              name="accident_date"
              label="Accident Date"
              control={control}
              rules={{ required: watch("accident") }}
              isRequired={watch("accident")}
              placeholder="dd/mm/yyyy"
              selected={watch("accident_date")}
            />
          </Col>

          <Col xs={12} sm={6} md={4} xl={3}>
            <label className="form-label">
              Accident Type <span className="text-danger">*</span>
            </label>
            <Controller
              name="accident_type"
              control={control}
              rules={{ required: watch("accident") }}
              render={({ field }) => (
                <MultiSelect
                  {...field}
                  isMulti={false}
                  isClearable
                  menuPlacement="auto"
                  closeMenuOnSelect={false}
                  placeholder="Choose ..."
                  value={
                    field.value
                      ? {
                        value: field.value.accdnttype_code,
                        label: field.value.accdnttype_name,
                      }
                      : null
                  }
                  onChange={(selected) =>
                    setValue(
                      "accident_type",
                      selected
                        ? {
                          accdnttype_code: selected.value,
                          accdnttype_name: selected.label,
                        }
                        : null
                    )
                  }
                  formatOptionLabel={({ label }) => <div>{label}</div>}
                  getOptionValue={(option) => option.value}
                  options={accidentType?.map((item) => ({
                    value: item.accdnttype_code,
                    label: item.accdnttype_name,
                  }))}
                  isDisabled={isAccident}
                />
              )}
            />
          </Col>

          <Col xs={12} md={6}>
            <TextInput
              name="address"
              control={control}
              label="Address"
              size="sm"
              placeholder="Address"
            />
          </Col>
        </Row>
      )}
    </div>
  );
};
const typeOptions = [
  { label: "Select Type", value: "" },
  { label: "Yes", value: "y" },
  { label: "No", value: "n" },
  { label: "Unknown", value: "u" },
];
